import { z } from "zod";
import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { DatePickerField, InputField } from "@/features/FormCard/Fields";
import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";
import { FormActionsNew } from "@/features/FormCard/Actions";
import { NewDiff } from "@/features/FormCard/NewDiff";
import { useEditForm } from "@/features/Edit/hooks";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import { dateSchema, parseToDateValue } from "@/features/FormCard/utils";
import { fieldTranslations } from "./translations";
import { useStrategyCurrency } from "./useStrategyCurrency";

const FormSchema = z.object({
  strategy_from_id: z.coerce.number().nullable().optional(),
  strategy_to_id: z.coerce.number().nullable().optional(),
  date: z.preprocess((val) => parseToDateValue(val), dateSchema),
  quantity: z.coerce.number().nullable().optional(),
  weight: z.coerce.number().nullable().optional(),
  market_value_local_ccy: z.coerce.number().nullable().optional(),
  market_value_ptf_ccy: z.coerce.number().nullable().optional(),
});
const formSchemaFields = FormSchema.keyof().enum;

const client = Clients.C7;
const table_name = Tables.C7_POSITIONS;

export function C7PositionsNew() {
  const { t } = useTranslation([Tables.C7_POSITIONS, "tables"]);
  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({ client, table_name, FormSchema, isNew: true });

  const strategyFromId = form.watch("strategy_from_id");
  const strategyToId = form.watch("strategy_to_id");
  const strategyFromCurrency = useStrategyCurrency(strategyFromId);
  const strategyToCurrency = useStrategyCurrency(strategyToId);

  const navigateToList = () => navigate(-1);

  return (
    <div className="grid place-items-center bg-gray-50 h-full w-full">
      <div className="flex justify-center w-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardHeader className="text-lg font-bold text-gray-700">
                {t("actions.new")}{" "}
                {t(`${table_name}.title`, {
                  ns: "tables",
                  defaultValue: "Position",
                })}
              </CardHeader>
              <Divider />

              <CardBody className="flex flex-col gap-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_from_id}
                    label={t(`columns.strategy_from_id`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Stratégie (depuis)",
                    })}
                    control={form.control}
                    endContent={
                      strategyFromCurrency ? (
                        <span className="text-xs text-default-400 whitespace-nowrap">
                          {strategyFromCurrency}
                        </span>
                      ) : undefined
                    }
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.strategy_to_id}
                    label={t(`columns.strategy_to_id`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Stratégie (vers)",
                    })}
                    control={form.control}
                    endContent={
                      strategyToCurrency ? (
                        <span className="text-xs text-default-400 whitespace-nowrap">
                          {strategyToCurrency}
                        </span>
                      ) : undefined
                    }
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchemaFields.date}
                    label={t(`columns.date`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Date",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.quantity}
                    label={t(`columns.quantity`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Quantité",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.weight}
                    label={t(`columns.weight`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Poids",
                    })}
                    control={form.control}
                  />
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_local_ccy}
                    label={t(`columns.market_value_local_ccy`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Valeur marché (devise locale)",
                    })}
                    control={form.control}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField
                    type="number"
                    name={formSchemaFields.market_value_ptf_ccy}
                    label={t(`columns.market_value_ptf_ccy`, {
                      ns: Tables.C7_POSITIONS,
                      defaultValue: "Valeur marché (devise ptf)",
                    })}
                    control={form.control}
                  />
                </div>
              </CardBody>

              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>

        <ConfirmationModal
          isOpen={isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
        >
          {changedFields
            ? NewDiff(changedFields, [], fieldTranslations, table_name)
            : t("no_changes")}
        </ConfirmationModal>

        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
